/**
 * Offer Context Utilities
 * 
 * Centralized utility functions for creating offer context objects
 * to ensure consistency across all offer-related operations.
 */

import { usePersistedDataStore } from '../components/store';

/** Court date scope for counterparty link / town portal (null = muni-wide, not "All"). */
export const resolveCourtDateForContext = (negotiationObj, user) => {
  const CourtDate = negotiationObj?.CourtDate;
  if (CourtDate && CourtDate !== 'All') return CourtDate;
  const court_date = user?.court_date;
  if (court_date) return court_date;
  return null;
};

// This function should probably just be eliminated and moved directly into the offer setting functions.
/**
 * Creates a standardized offer context object with all required fields
 * @param {Object} user - User object with userId and userAccess
 * @param {Object} negotiationObj - Optional negotiation object override
 * @param {string} offerSource - How the offer was set ('Manual', 'Random', 'Workup', etc.)
 * @param {string} evidenceTypeOverride - Optional evidence type override (for evidence_comparison mode)
 * @returns {Object} Standardized offer context
 */
export const createOfferContext = (user, negotiationObj = null, offerSource = 'Manual', evidenceTypeOverride = null) => {
  const getNegotiationObj = negotiationObj || usePersistedDataStore.getState().negotiationObj;
  
  // Use override if provided, otherwise determine from reportType
  let evidenceType;
  if (evidenceTypeOverride) {
    evidenceType = evidenceTypeOverride;
  } else {
    evidenceType = getNegotiationObj.reportType === 'standard' ? 'Sales' : 
                   getNegotiationObj.reportType === 'assessment' ? 'Assessments' : 'Sales';
  }
  
  return {
    muniCode: getNegotiationObj.MuniCode,
    taxYear: getNegotiationObj.TaxYear,
    courtDate: resolveCourtDateForContext(getNegotiationObj, user),
    caseType: getNegotiationObj.villageRun === 1 ? 'Village' : 'Town',
    evidenceType: evidenceType,
    villageRun: getNegotiationObj.villageRun,
    userId: user?.userId,
    userAccess: user?.userAccess,
    offerSource: offerSource
  };
};

/**
 * Creates a minimal context object for non-offer operations that still need basic location info
 * @param {Object} negotiationObj - Optional negotiation object override
 * @returns {Object} Minimal context with basic location info
 */
export const createMinimalContext = (negotiationObj = null) => {
  const getNegotiationObj = negotiationObj || usePersistedDataStore.getState().negotiationObj;
  
  return {
    muniCode: getNegotiationObj.MuniCode,
    taxYear: getNegotiationObj.TaxYear,
    villageRun: getNegotiationObj.villageRun
  };
};

/**
 * Creates a context object for case status operations
 * @param {Object} user - User object with userId
 * @param {string} reviewType - Type of review (e.g., 'Negotiation', 'CaseReview')
 * @param {Object} negotiationObj - Optional negotiation object override
 * @returns {Object} Context for case status operations
 */
export const createCaseStatusContext = (user, reviewType, negotiationObj = null) => {
  const getNegotiationObj = negotiationObj || usePersistedDataStore.getState().negotiationObj;
  
  return {
    muniCode: getNegotiationObj.MuniCode,
    taxYear: getNegotiationObj.TaxYear,
    reviewType,
    userId: user?.userId,
    villageRun: getNegotiationObj.villageRun || 0,
  };
};

/**
 * Creates a context object for court files operations
 * @param {Object} user - User object with userId
 * @param {Object} negotiationObj - Optional negotiation object override
 * @returns {Object} Context for court files operations
 */
export const createCourtFilesContext = (user, negotiationObj = null) => {
  const getNegotiationObj = negotiationObj || usePersistedDataStore.getState().negotiationObj;
  
  return {
    muniCode: getNegotiationObj.MuniCode,
    taxYear: getNegotiationObj.TaxYear,
    userId: user?.userId
  };
};
